import React, { useEffect, useRef, useState } from "react";
import Footer from "../Footer";
import { getPractice, notify } from "../../helpers";
import { useLocation } from "react-router-dom";
import { fetchCallNoteAllQuestions, setupDevice } from "../../services/callNotes";
import { Device } from "@twilio/voice-sdk";

export default function CallNote() {
  var locationId = getPractice("location_id");
  var accountId = getPractice("account_id");

  const [isCalling, setIsCalling] = useState(false);
  const [device, setDevice] = useState(null);
  const [call, setCall] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callNoteQuestionData, setCallNoteQuestionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);
  const isFetched = useRef(false);
  const callIntervalRef = useRef(null); 

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const patientName = queryParams.get('name');
  const patientPhone = queryParams.get('phone');
  const patientId = queryParams.get('patient_id');
  const callType = queryParams.get('call_type');
  
  const handleMouseDown = (e) => {
    setDragging(true);
    setOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;

    const newX = e.clientX - offset.x;
    const newY = e.clientY - offset.y;

    setPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  useEffect(() => {
    if (!isFetched.current && patientId && callType) {
      if(callType === "ccm") {
        fetchCallNoteQuestions(patientId);
      } else {
        setLoading(false);
      }
      isFetched.current = true;
    }
    return () => {
      clearInterval(timerRef.current);
    };
  }, [patientId]);

  const fetchCallNoteQuestions = async (patient_id) => {
    setLoading(true);
    try {
      const questionData = {
        account_id: accountId,
        location_id: locationId,
        patient_id: patient_id,
      };

      const response = await fetchCallNoteAllQuestions(questionData);
      if(response){
        if(response?.error) {
          notify("error", response?.error);
          setTimeout(() => {
            window.close();
          }, 2000);
          return;
        }
        if(response?.data) {
          setCallNoteQuestionData(response?.data);
        } else {
          notify("error", response?.detail);
          setTimeout(() => {
            window.close();
          }, 2000);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ fetchCallNoteAllQuestions ~ error:", error);
      setTimeout(() => {
        window.close();
      }, 2000);
    } finally {
      setLoading(false);
    }
  }

  const SplitGridFeaturs = (sortedResponse, parts) => {
    if (!Array.isArray(sortedResponse) || sortedResponse.length === 0) {
      return Array.from({ length: parts }, () => []);
    }
  
    const chunkSize = Math.ceil(sortedResponse.length / parts);
    return Array.from({ length: parts }, (_, i) =>
      sortedResponse?.slice(i * chunkSize, i * chunkSize + chunkSize)
    );
  };

  const [firstColumn, secondColumn] = SplitGridFeaturs(callNoteQuestionData, 2);

  const fetchTokenAndSetupDevice = async () => {
    try {
      const response = await setupDevice();

      if(response) {
        const { token, identity } = response.data;

        const newDevice = new Device(token, { debug: true });

        newDevice.on("ready", () => {
          console.log(`Twilio Device ready for identity: ${identity}`);
        });
        newDevice.on("error", (error) => {
          console.error("Twilio Device error:", error);
        });
        newDevice.on("connect", () => {
          console.log("Twilio Device connected.");
          notify("success", "Call connected.");
          setIsConnected(true);
          startCallTimer();
          setIsCalling(false);
        });

        newDevice.on("disconnect", () => {
          console.log("Twilio Device disconnected.");
          notify("info", "Call disconnected.");
          setIsConnected(false);
          stopCallTimer();
          setIsCalling(false);
        });

        setDevice(newDevice);
      }

    } catch (error) {
      console.error("Failed to fetch Twilio token:", error);
      notify("error", "Failed to initialize Twilio. Please try again.");
    }
  };

  useEffect(() => {
    fetchTokenAndSetupDevice();
    return () => {
      if (device) {
        device.destroy();
        setDevice(null);
      }
      stopCallTimer(); // Ensure the timer is stopped on cleanup
    };
  }, []);
  
  const handleCall = async () => {
    if (!device) {
      notify("error", "Device not initialized. Please refresh and try again.");
      return;
    }
    setIsCalling(true); // Show calling state
    const params = { To: patientPhone, Test: true, AccountID: accountId, LocationID: locationId, PatientID: patientId }; // Pass the patient's phone number as a parameter
    try {
      console.log("Calling with params:", params);
      const newCall = await device.connect({ params });
      notify("success", "Calling...");
      // setIsCalling(true); // Show calling state
      setIsConnected(true);
      setCall(newCall);
      startCallTimer(); // Start the call timer

      // Attach event listener for disconnect
      newCall.on("disconnect", handleDisconnect);

    } catch (error) {
      console.error("Error while calling device.connect:", error);
      notify("error", "Failed to initiate call. Please try again.");
      setIsCalling(false); // Reset calling state on error
    }
  };

  const handleDisconnect = () => {
    if (device) {
      device.disconnectAll();
      setIsConnected(false); // Explicitly set isConnected to false on disconnect
      setIsCalling(false); // Explicitly set isCalling to false on disconnect
      stopCallTimer();
      setTimeout(() => {
        window.close();
      }, 2000);
    }
  };

  const handleMuteToggle = async () => {
    if (device && call) {
      try {
        const resolvedCall = await call; // Await the resolved promise
        console.log("Call object:", resolvedCall);
  
        if (resolvedCall) {
          if (isMuted) {
            resolvedCall.mute(false); // Unmute
            notify("success", "Unmuted.");
          } else {
            resolvedCall.mute(true); // Mute
            notify("success", "Muted.");
          }
          setIsMuted(!isMuted);
        }
      } catch (error) {
        console.error("Error toggling mute:", error);
        notify("error", "Unable to toggle mute. Please try again.");
      }
    } else {
      console.error("Device or call not initialized.");
    }
  };

  const startCallTimer = () => {
    if (callIntervalRef.current) return; // Prevent multiple intervals
    callIntervalRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  };

  const stopCallTimer = () => {
    if (callIntervalRef.current) {
      clearInterval(callIntervalRef.current);
      callIntervalRef.current = null;
    }
    setCallDuration(0); // Reset the call duration
  };

  const formatCallDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const LimitText = ({ text, limit }) => {
    if (text.length <= limit) {
      return text;
    } else {
      const limitText = text.substring(0, limit) + "...";
      return limitText;
    }
  };

  const QuestionList = ({ items, startIndex = 0 }) =>
    items?.map((item, index) => (
      <div key={index}>
        <p className="docpro_mb_0"><b className="docpro_accent_color" style={{ textTransform: 'captionSide' }}>{startIndex + index + 1}. {item?.condition} </b></p>
        <p style={{ marginLeft: '20px' }}>{item?.question}</p>
      </div>
    )
  );

  return (
    <>
      <div className="docpro_calling_screen">
        <>
          <div className="docpro_calling_question">
            <div className="question_section">
              {loading ? (
                <div
                  style={{
                    height: "360px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  CCM Questions...
                </div>
              ) : (
                callNoteQuestionData?.length > 0 && (
                  <QuestionList items={firstColumn} startIndex={0} />
                )
              )}
            </div>
            <div className="question_section">
              {loading ? (
                <div
                  style={{
                    height: "360px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  CCM Questions...
                </div>
              ) : (
                callNoteQuestionData?.length > 0 && (
                  <QuestionList items={secondColumn} startIndex={firstColumn?.length} />
                )
              )}
            </div>
          </div>
          <div className="docpro_callnote_card"
            style={{
              transform: `translate(${position.x}px, ${position.y}px)`,
              cursor: dragging ? "grabbing" : "grab",
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp} 
          >
            <>
              <div className="docpro_callnote_header">
                <div className="docpro_callnote_animation">
                  <span className={`icon ring ${isCalling ? 'ring_call' : ''}`}></span>
                  <div className={`cercle one ${isCalling ? 'wave' : ''}`}></div>
                  <div className={`cercle two ${isCalling ? 'wave' : ''}`}></div>
                  <div className={`cercle three ${isCalling ? 'wave' : ''}`}></div>
                </div>
                <p className="docpro_callnote_name">
                  <LimitText text={patientName || ""} limit={25} />
                </p>
                <p className="docpro_callnote_phone">{patientPhone || ""}</p>
                {isCalling &&
                  <p className="docpro_callnote_calling">Calling ...</p>
                }
                {isConnected && !isCalling && (
                  <>
                    <p className="docpro_callnote_received">Received</p>
                    <p className="docpro_callnote_calling">{formatCallDuration(callDuration)}</p>
                  </>
                )}
              </div>

              <div className="docpro_callnote_footer">
                {!isConnected && !isCalling && (
                  <div className="docpro_callnote_bouton docpro_callnote_decrocher" onClick={() => handleCall()}>
                    <span className="icon docpro_callnote_green"></span>
                  </div>
                )}
                {isConnected && (
                  <>
                    <div className="docpro_callnote_bouton3 docpro_callnote_raccrocher3" onClick={() => handleMuteToggle()}>
                      {isMuted ? 
                        <span className="icon docpro_callnote_mic_off"></span>
                      : 
                        <span className="icon docpro_callnote_mic"></span>
                      }
                    </div>
                    <div className="docpro_callnote_bouton2 docpro_callnote_raccrocher" onClick={() => handleDisconnect()}>
                      <span className="icon docpro_callnote_red"></span>
                    </div>
                  </>
                )}
              </div>
            </>
          </div>
        </>
      </div>
      <div className="docpro_callnote_credit">
        <Footer />
      </div>
    </>
  );
}
